
import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import type { Associate, Project, Settings } from '../../types';
import { useDocumentBySlug } from '../../hooks/useDocumentBySlug';
import ProjectCard from '../../components/ProjectCard';
import OptimizedImage from '../../components/OptimizedImage';
import { useSEO } from '../../hooks/useSEO';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import HeroSlider from '../../components/HeroSlider';

const ProjectCardSkeleton: React.FC = () => (
    <div className="group relative overflow-hidden cursor-pointer aspect-[4/3] block bg-gray-200 animate-pulse">
        <div className="absolute bottom-0 left-0 p-6 w-full">
            <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
            <div className="h-8 bg-gray-400 rounded w-2/3"></div>
        </div>
    </div>
);

interface AssociatePortfolioPageProps {
    settings: Settings;
}

const AssociatePortfolioPage: React.FC<AssociatePortfolioPageProps> = ({ settings }) => {
    const { slug } = useParams<{ slug: string }>();
    const { document: associate, isLoading: isLoadingAssociate } = useDocumentBySlug<Associate>('associates', slug);
    const [activeCategory, setActiveCategory] = useState<string>('All');
    
    const [projects, setProjects] = useState<Project[]>([]);
    const [isLoadingProjects, setIsLoadingProjects] = useState(true);

    const featuredProjects = useMemo(() => {
        return projects.filter(p => p.featured);
    }, [projects]);


    useSEO({
        title: associate ? `${associate.name} | ${settings.companyName}` : undefined,
        description: associate?.description,
        ogImage: associate?.logoUrl
    }, [associate, settings]);

    useEffect(() => {
        if (!slug) return;

        const fetchProjects = async () => {
            setIsLoadingProjects(true);
            try {
                const q = query(collection(db, 'projects'), where('associateSlug', '==', slug));
                const snapshot = await getDocs(q);
                const fetchedProjects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Project));
                
                // Filter for published and sort on client side for robustness
                const finalProjects = fetchedProjects
                    .filter(p => p.published)
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                    
                setProjects(finalProjects);
            } catch (error) {
                console.error("Error fetching associate projects:", error);
                setProjects([]);
            } finally {
                setIsLoadingProjects(false);
            }
        };

        fetchProjects();
    }, [slug]);

    const categories = useMemo(() => {
        if (!projects) return ['All'];
        const uniqueCategories = [...new Set(projects.map(p => p.category))];
        return ['All', ...uniqueCategories];
    }, [projects]);

    const filteredProjects = useMemo(() => {
        if (activeCategory === 'All') {
            return projects;
        }
        return projects.filter(p => p.category === activeCategory);
    }, [projects, activeCategory]);

    if (isLoadingAssociate) {
        return <div className="h-screen flex items-center justify-center"><p>Loading associate portfolio...</p></div>;
    }

    if (!associate) {
        return <Navigate to="/associates" />;
    }

    const heroFallback = (
        <>
            <div className="absolute inset-0 bg-brand-dark"></div>
            <div className="absolute inset-0 bg-black bg-opacity-60"></div>
            <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white p-4">
                <div className="animate-fade-in-slow">
                    <OptimizedImage
                        src={associate.logoUrl}
                        size={400}
                        alt={`${associate.name} logo`}
                        className="w-32 h-32 md:w-40 md:h-40 object-contain mb-6"
                        fallbackSrc={`https://ui-avatars.com/api/?name=${encodeURIComponent(associate.name)}&background=e5e7eb&color=4b5563&size=256`}
                    />
                    <h1 className="text-4xl md:text-6xl font-bold my-4 font-heading">{associate.name}</h1>
                    <p className="max-w-2xl mx-auto text-lg text-gray-200 font-body">{associate.description}</p>
                </div>
            </div>
        </>
    );

    return (
        <div className="bg-brand-light text-brand-dark pb-16">
            <HeroSlider projects={featuredProjects} fallback={heroFallback} />

            <div className="container mx-auto px-6 max-w-5xl pt-16 space-y-20">
                {associate.aboutUs && (
                    <section>
                        <h2 className="text-3xl font-bold font-heading text-center mb-8">About {associate.name}</h2>
                        <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: associate.aboutUs }}></div>
                    </section>
                )}

                {(associate.vision || associate.mission) && (
                    <section className="grid md:grid-cols-2 gap-12 bg-gray-100 p-12 rounded-lg">
                        {associate.vision && (
                            <div>
                                <h3 className="text-2xl font-bold font-heading mb-4">Our Vision</h3>
                                <p className="text-gray-700 leading-relaxed font-body">{associate.vision}</p>
                            </div>
                        )}
                        {associate.mission && (
                            <div>
                                <h3 className="text-2xl font-bold font-heading mb-4">Our Mission</h3>
                                <p className="text-gray-700 leading-relaxed font-body">{associate.mission}</p>
                            </div>
                        )}
                    </section>
                )}

                {associate.services && (
                    <section>
                        <h2 className="text-3xl font-bold font-heading text-center mb-8">Our Services</h2>
                        <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: associate.services }}></div>
                    </section>
                )}

                {(associate.address || associate.email || associate.companyProfileUrl) && (
                    <section className="text-center bg-gray-100 p-12 rounded-lg">
                         <h2 className="text-3xl font-bold font-heading text-center mb-8">Get In Touch</h2>
                         <div className="space-y-2 text-lg text-gray-700 font-body">
                            {associate.address && <p>{associate.address}</p>}
                            {associate.email && <a href={`mailto:${associate.email}`} className="hover:underline">{associate.email}</a>}
                         </div>
                         {associate.companyProfileUrl && (
                             <a href={associate.companyProfileUrl} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-3 px-8 py-3 bg-brand-dark text-brand-light font-semibold uppercase tracking-widest hover:bg-gray-800 transition-colors font-header">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                Download Company Profile
                             </a>
                         )}
                    </section>
                )}

                {/* Portfolio Section */}
                <div className="pt-16 border-t">
                    <h2 className="text-3xl font-bold font-heading text-center mb-12">Projects in Collaboration</h2>
                    <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 md:gap-x-8 mb-12 font-header">
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setActiveCategory(category)}
                                className={`px-2 py-2 text-sm uppercase tracking-wider font-semibold border-b-2 transition-colors duration-300 ${
                                    activeCategory === category 
                                        ? 'border-brand-dark text-brand-dark' 
                                        : 'border-transparent text-gray-500 hover:text-brand-dark'
                                }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
                        {isLoadingProjects && Array.from({ length: 3 }).map((_, i) => <ProjectCardSkeleton key={i} />)}
                        {filteredProjects.map(project => (
                            <ProjectCard key={project.id} project={project} />
                        ))}
                    </div>
                    
                    {!isLoadingProjects && filteredProjects.length === 0 && (
                        <p className="text-center col-span-full text-gray-500 py-12">
                            {activeCategory === 'All' ? `No published projects have been assigned to ${associate.name} yet.` : 'No projects found in this category.'}
                        </p>
                    )}
                </div>
            </div>
             <style>{`
                .prose p { margin-bottom: 1.25em; }
                .prose h1, .prose h2, .prose h3 { font-family: var(--font-family-heading); }
                .prose ul, .prose ol { margin-left: 1.5em; }
                .prose-invert p, .prose-invert h3 { color: #d1d5db; }
                 @keyframes fade-in-slow {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in-slow { animation: fade-in-slow 1s ease-out forwards; }
            `}</style>
        </div>
    );
};

export default AssociatePortfolioPage;
